import angular from "angular";

import { CalculationListItem, Calculations, DataMappersLogic, EmploymentLogic, EmploymentRelationSalaryPaymentMethod, EmploymentRelationTaxcard, EmploymentRelationType, Employments, EnumerationsLogic, Exportable, InsuranceCompany, Numeric, PensionCompany, TaxcardKind, Translations, UserSession } from "@salaxy/core";
import { AccountSettingsInSession, ContactAdvanced, ContactType, Country, Employment, EmploymentListItem, EmploymentSocialSecurityModel, WorkerAccount, Objects } from "@salaxy/core";
import { InternationalSocialSecurityException, PensionCalculation } from "@salaxy/core";

import { FileService, JsonSchemaService, SessionService, UiCrudHelpers, UiHelpers } from "../../services";
import type { EmploymentDetailsTabs } from "../../services";
import { ApiCrudObjectController } from "../bases";
import { EmploymentTaxcardsController } from "./EmploymentTaxcardsController";

/**
 * CRUD functionality for Employment relation.
 */
export class EmploymentCrudController extends ApiCrudObjectController<Employment, EmploymentListItem> {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["Employments", "SessionService", "FileService", "JsonSchemaService", "UiCrudHelpers", "UiHelpers", "$location", "$routeParams", "$timeout", "Calculations", "$route"];

  /** If true, the save/delete etc. buttons are not shown (they will come from the container / modal) */
  public isModal: boolean;

  /**
   * If set, the Worker details component can show infromation about the latest taxcard in the view.
   * This is not present in the employment, but taxcards controller may be present in the same view.
   * @example
   * In the Details.html view we set:
   * <salaxy-employment-taxcards employment-id="$ctrl.current.id" ng-ref="$ctrl.taxcardsController"></salaxy-employment-taxcards>
   */
  public taxcardsController: EmploymentTaxcardsController;

  public employmentForm: angular.IFormController;

  constructor(
    private fullApi: Employments,
    private sessionService: SessionService,
    private fileService: FileService,
    private jsonSchemaService: JsonSchemaService,
    private uiCrudHelpers: UiCrudHelpers,
    uiHelpers: UiHelpers,
    $location: angular.ILocationService,
    $routeParams: any,
    private $timeout: angular.ITimeoutService,
    private calculations: Calculations,
    private $route: angular.route.IRouteService,
  ) {
    super(fullApi, uiHelpers, $location, $routeParams);
  }

  /**
   * Initialization code.
   */
  public $onInit() {
    super.$onInit();
  }

  /** Implements the getDefaults of ApiCrudObjectController */
  public getDefaults() {
    return {
      listUrl: this.listUrl || "/workers",
      detailsUrl: this.detailsUrl || "/employments/details/",
      oDataTemplateUrl: "salaxy-components/odata/lists/Employments.html",
      oDataOptions: {},
    };
  }

  /**
   * Gets or sets the taxcard type in employment.
   * In set, shows a confirm dialog before committing the change.
   */
  public get taxcardType(): EmploymentRelationTaxcard {
    return this.current.info.taxcard;
  }
  public set taxcardType(value: EmploymentRelationTaxcard) {
    if (!this.current?.info || this.current.info.taxcard == value) {
      return;
    }
    if ((this.current.info?.taxcard || EmploymentRelationTaxcard.Undefined) == EmploymentRelationTaxcard.Undefined) {
      this.current.info.taxcard = value;
      this.save();
      return;
    }
    this.uiHelpers.showConfirm("SALAXY.UI_Terms.areYouSure", "SALAXY.NG1.WorkerDetailsComponent.taxCard.typeAreYouSure").then((result) => {
      if (result) {
        this.current.info.taxcard = value;
        this.save();
      }
    });
  }

  /** Returns true if the employer is a household employer (person as opposed to company). */
  public get isHousehold() {
    return this.sessionService.isInRole("person");
  }

  /**
   * Gets a description text for an employment relation
   * @param employment The employment relation to describe.
   */
  public getEmploymentDescription(employment: Employment | EmploymentListItem): string {
    if (EmploymentLogic.isListItem(employment)) {
      return EmploymentLogic.getDescription(employment.otherPartyInfo, employment.data.type, employment.data.pensionCalculation);
    }
    return EmploymentLogic.getDescription(employment.personalInfo, employment.info?.type, employment.info?.pensionCalculation);
  }

  /**
   * Gets a descriptive text on whether the employment is active or not.
   * @param employment Employment relation to get the text for.
   * @returns A text that describes whether the employment is active or not with the dates (start and end).
   */
  public getActiveText(employment: Employment | EmploymentListItem): string {
    return EmploymentLogic.getActiveText(employment);
  }

  /**
   * Gets the tax card info text for active taxcard.
   */
  public getTaxCardInfo(): {
    /** Text that is shown to the end user. */
    text: string,
    /** If true, the taxcard is missing => show as error. */
    isMissing: boolean,
  } | undefined {
    const result = {
      text: Translations.get("SALAXY.UI_Terms.loading"),
      isMissing: false,
    };
    if (!this.current) {
      return result;
    }
    if (this.current.info.taxcard == EmploymentRelationTaxcard.NoTaxCard) {
      result.text = EnumerationsLogic.getEnumLabel("TaxcardKind", TaxcardKind.NoTaxCard);
      return result;
    }
    if (this.current.info.taxcard == EmploymentRelationTaxcard.TaxAtSource) {
      result.text = EnumerationsLogic.getEnumLabel("EmploymentRelationTaxcard", EmploymentRelationTaxcard.TaxAtSource);
      result.text = `${result.text} ${Numeric.formatPercent(this.current.international.taxAtSource.percent * 100, null)}`;
      return result;
    }
    if (!this.taxcardsController?.current) {
      return result;
    }
    const taxcardData = this.taxcardsController.current;
    const taxCard = taxcardData.active;
    if (!taxCard && taxcardData.employmentTaxcard == EmploymentRelationTaxcard.Auto) {
      result.text = EnumerationsLogic.getEnumLabel("TaxcardKind", TaxcardKind.Auto);
    } else {
      const kind = taxCard ? taxCard.card.kind : TaxcardKind.Undefined;
      result.isMissing = kind == TaxcardKind.Undefined;
      if (kind === TaxcardKind.Undefined || kind === TaxcardKind.NoTaxCard || kind === TaxcardKind.NoWithholdingHousehold) {
        result.text = EnumerationsLogic.getEnumLabel("TaxcardKind", kind);
      } else {
        const percent = taxCard?.card?.taxPercent === 0 ? 0 : (taxCard?.card?.taxPercent || 60);
        result.text = EnumerationsLogic.getEnumLabel("TaxcardKind", kind) + ": " + Numeric.formatPercent(percent);
      }
    }
    return result;
  }

  /**
   * Gets a contact of specific type.
   * @param type Type of contact to get. Use null to fetch any contact type (creates Default if requested).
   * @param createIfNotExists If true creates the contact of given type if one does not exist.
   * @returns Contact of given type or undefined if one is not found AND createIfNotExists is false.
   */
  public getContact(type: ContactType | null, createIfNotExists = true): ContactAdvanced | null {
    return EmploymentLogic.getContact(this.current, type || ContactType.Default, createIfNotExists);
  }

  private _readOnlyDefaultContact = {};
  /**
   * Returns default contact as read only.
   */
  public get readOnlyDefaultContact() {
    const contact = this.getContact(ContactType.Default) ?? {};
    if (!Objects.equal(contact, this._readOnlyDefaultContact)) {
        this._readOnlyDefaultContact = { ...contact };
    }
    return this._readOnlyDefaultContact;
  }

  /**
   * Gets the text for a Taxcard type badge (short text in lists).
   * Returns null if the badge should not be shown.
   */
  public getTaxcardBadge(cardType: EmploymentRelationTaxcard): string | null {
    switch (cardType) {
      case EmploymentRelationTaxcard.Auto:
        return "Auto";
      case EmploymentRelationTaxcard.Manual:
        return "Manual";
      case EmploymentRelationTaxcard.NoTaxCard:
        return "60%";
      case EmploymentRelationTaxcard.TaxAtSource:
      default:
        return EnumerationsLogic.getEnumLabel("EmploymentRelationTaxcard", cardType);
    }
  }

  /**
   * Opens the new Employment relation wizard and if a new relation is created, moves the user to the list view.
   */
  public launchCreateEmploymentWizard() {
    this.uiCrudHelpers.createNewEmployment().then((result) => {
      if (result.action === "ok") {
        const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");
        return this.uiHelpers.wait(3000).then(() => {
          loading.dismiss();
          this.$route.reload();
        });
      }
    });
  }

  /**
   * Shows a dialog for editing an existing employment.
   * If user clicks OK, the changes are saved.
   * @param employmentId - Identifier of the Workers employment.
   * @param buttonTypes "default" shows standard save button,
   * "updateCalc" shows two buttons "ok" for full calc update and "ok-no-rows" update without touching default rows.
   * @param initialTab - Tab to open.
   * @returns Dialog result. Note that OK result may be either "ok" or "ok-no-rows".
   * The latter means that for calculation only worker info should be updated, not the default rows.
   */
  public openEditEmploymentDialog(employmentId: string, buttonTypes: "default" | "updateCalc", initialTab: EmploymentDetailsTabs = null) {
    return this.uiCrudHelpers.openEditEmploymentDialog(employmentId, buttonTypes, initialTab);
  }

  /**
   * Returns Exportable api for the used crud.
   * @returns Exportable api.
   */
  public getExportable(): Exportable<Employment> {
    return {
      getExportMappers: () => DataMappersLogic.getMappers(["Employment", "EmploymentListItem"]),
      getExportMapper: (id) => DataMappersLogic.getBuiltInMapper(id, this.jsonSchemaService.getCache()),
      exporters: this.fileService.getDefaultExporters(),
      getSingle: (item) => this.getApi().getSingle(item.id),
      list: (maxCount?, notify?) => this.odataController.exportItems(maxCount ?? 0, notify),
    };
  }

  /**
   * Deletes the current employment if there are no associated calculations.
   * This method checks for any calculations associated with the current employment.
   * If no calculations are found, it proceeds to delete the employment.
   * Otherwise, it shows an alert indicating that the employment cannot be deleted because there are associated calculations that need to be removed first.
   */
  public async deleteEmployment(): Promise<void> {
      const result = await this.calculations.getOData({ $filter: `data/employmentId eq '${this.current.id}'` });
      // If there are no calculations, delete the employment.
      if (result.items.length === 0) {
        await this.delete(this.current, Translations.get("SALAXY.NG1.WorkerListComponent.employmentDeletion.confirmText"));
        return;
      }

      this.uiHelpers.showAlert(
        Translations.get("SALAXY.NG1.WorkerListComponent.employmentDeletion.existingtCalculationsHeading"),
        Translations.get("SALAXY.NG1.WorkerListComponent.employmentDeletion.existingtCalculationsText")
      );
  }

  /**
   * Opens a list of employment relations in to a dialog window.
   * Creates list of selected employments and updates taxcard to auto.
   * @param items Optional array of currently loaded/visible items.
   * The data.taxcard is updated so that the changes are refelected in the list.
   */
  public selectAutoTaxcardForEmployments(items?: EmploymentListItem[]) {
    const employmentList: EmploymentListItem[] = [];
    this.uiHelpers.openEditDialog(
      "salaxy-components/modals/calc/WorkerList.html",
      employmentList,
      {
        title: "SALAXY.NG1.PayrollComponent.createNewCalcDialog",
        okButtonText: "SALAXY.NG1.WorkerListComponent.okButtonText",
        infoHtml: "SALAXY.NG1.WorkerListComponent.infoText.html"
      }).then((result) => {
        if (result.action === "ok" && employmentList.length > 0) {
          const loader = this.uiHelpers.showLoading("SALAXY.UI_Terms.isSaving");
          let counter = 1;
          employmentList.forEach(selectedItem => {
            loader.setText(`${Translations.get("SALAXY.UI_Terms.isSaving")} ${counter}/${employmentList.length}`);
            this.fullApi.getSingle(selectedItem.id).then((fullEmployment) => {
              fullEmployment.info.taxcard = EmploymentRelationTaxcard.Auto;
              this.fullApi.save(fullEmployment).then((savedEmployment) => {
                if (items) {
                  const item = items.find(x => x.id === savedEmployment.id);
                  if (item) {
                    item.data.taxcard = savedEmployment.info.taxcard;
                  }
                }
                counter++;
                if (counter - 1 === employmentList.length) {
                  loader.dismiss();
                }
              });
            });
          });
        }
      });
  }

  /**
   * Called when the type of pension calculation changes
   * => Sets the PartialOwner and NoPensionRequired IncomeEarnerTypes accordingly.
   */
  public pensionCalculationChange() {
    EmploymentLogic.resetModelFromPensionCalculation(this.current);
  }

  /** Gets the session - currently used for company type */
  public get session(): UserSession {
    return this.sessionService.session;
  }

  /** Called when the type of employment changes => Sets the income earner types accordingly. */
  public employmentTypeChange(): void {
    EmploymentLogic.resetModelFromType(this.current);
  }

  /** Gets the cached version of the social security selection. */
  public get socSec() {
    return this.uiHelpers.cache(this, "socSec",
      () => this.getSocialSecurityModel(
        this.sessionService.session?.settings, this.current.info.type, this.current.international.socialSecurity, this.current.info.pensionCalculation,
        this.current.info.pensionCompany, this.current.info.insuranceCompany, this.current.international.socialSecurityExceptions),
      () => this.current.info.type + this.current.international.socialSecurity + this.current.info.pensionCalculation
        + this.current.info.pensionCompany + this.current?.info.insuranceCompany + this.current.international.socialSecurityExceptions
    );
  }

  /** Gets or sets the current country. When setting, the "foreign" address is reset. When getting Finland (fi) is converted to null. */
  public get country(): Country | null {
    return this.current.international.country == Country.Fi ? null : this.current.international.country;
  }
  public set country(value: Country) {
    this.current.international.country = value;
    this.current.contacts = (this.current.contacts || []).filter(x => x.type != ContactType.Foreign);
    const contact = this.getContact(ContactType.Foreign, true);
    contact.country = value;
  }

  /**
   * Gets the social security pensions model (available options) for the current selections.
   * @param settings Account settings in the session
   * @param type Type of employment relation selected
   * @param socialSecurity Whether the person is in Finnish or foregion social security - or mixed
   * @param pensionCalculation Type of Pension calculation: employee/TyEL or others (YEL, farmer etc.)
   * @param pensionCompany Pension company (if several) that is selected.
   * @param insuranceCompany Insurance company (if several) that is selected.
   * @param socSecExceptions The selected social security exceptions (not subject to)
   */
  public getSocialSecurityModel(
    settings: AccountSettingsInSession, type: EmploymentRelationType,
    socialSecurity: "default" | "foreign" | "mixed", pensionCalculation: PensionCalculation,
    pensionCompany: PensionCompany, insuranceCompany: InsuranceCompany,
    socSecExceptions: InternationalSocialSecurityException[],
  ): EmploymentSocialSecurityModel {
    return EmploymentLogic.getSocialSecurityModel(settings, type, socialSecurity, pensionCalculation, pensionCompany, insuranceCompany, socSecExceptions);
  }

  /** Overrides the Save method so that it realoads the taxcards controller if present. */
  public save(): Promise<WorkerAccount> {
    return super.save().then((workerAccount) => {
      if (this.taxcardsController) {
        this.taxcardsController.reload();
      }
      return workerAccount;
    });
  }

  /** Recalculate pay statistics for the current employment */
  public recalculatePayStats(): Promise<Employment> {
    const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");
    return this.fullApi.recalculateEmploymentPayStats(this.current.id).then( () => {
      loading.dismiss();
      return this.reload();
    });
  }

  /** Recalculate pay statistics for all employments */
  public recalculateAllPayStats() {
      return this.uiHelpers.showConfirm("Ansioiden laskenta",
      `Haluatko varmasti laskea keskituntiansiot kaikille työntekijöille?`).then( (result) => {
        if (result) {
          const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");
          return this.fullApi.recalculatePayStats().then( () => {
            loading.dismiss();
            return this.uiHelpers.showAlert("Ansioiden laskenta", "Keskituntiansioiden laskenta on käynnistetty. Laskenta tapahtuu taustalla, ja se voi kestää jonkin aikaa.");
          });
        }
        else {
          return result;
        }
    });
  }

  /**
   * Gets a tooltip that shows which fields are editied in the employment relation to be different
   * from the worker account (what the registered Worker has input in their UI).
   * @param inputName Name of the field in Contact or "ibanNumber".
   * @returns Tooltip text if there is a diff and tooltip should be shown.
   */
  public getInputTooltip(inputName: "email" | "telephone" | "street" | "postalCode" | "city" | "ibanNumber"): string | undefined {
    if (inputName == "ibanNumber") {
      if (this.current.info.ibanNumber !== this.current.personalInfo.ibanNumber) {
        return `Tilinumeroa on muokattu tähän työsuhteeseen. Rekisteröityneen työntekijän syöttämä arvo on "${this.current.personalInfo.ibanNumber}". Jos haluat käyttää työntekijän syöttämää arvoa, voit kopioida sen ja päivittää tiedon kenttään, tai tyhjentää kentän ja painaa Tallenna.`;
      }
      return undefined;
    }
    if (!EmploymentLogic.getContact(this.current, ContactType.EmployerOverride)) {
      // If not originally overridden, we do not wish to show this (user is editing now).
      return undefined;
    }
    const diff = EmploymentLogic.getContact(this.current, ContactType.EmployerOverride)?.[inputName];
    const workerAccountValue = EmploymentLogic.getContact(this.current, ContactType.WorkerAccount)?.[inputName];
    if (diff) {
      return `Yhteystietoa on muokattu tähän työsuhteeseen. Rekisteröityneen työntekijän syöttämä arvo on "${workerAccountValue}"`;
    }
    return undefined;
  }

  /**
   * Returns the true if the field is required for the more complex UI fields.
   * @param inputName Name of the field in Contact address (currently email and telephone never required) or "ibanNumber".
   * @returns True if required.
   */
  public isRequired(inputName: "street" | "postalCode" | "city" | "ibanNumber"): boolean {
    if (inputName == "ibanNumber") {
      if (this.current.info.salaryPaymentMethod != EmploymentRelationSalaryPaymentMethod.BankAccount) {
        return false;
      }
      // Required if worker account is editable (this value is updated to Worker account) OR if worker account value is empty.
      return !this.current.isRegistered || !this.current.personalInfo.ibanNumber;
    }
    // Address is required if Personal ID starts with 9 (keinohetu) AND the same as ibanNumber above (editable or worker account empty).
    return this.isOtherIdentifier && (!this.current.isRegistered || !this.getContact(ContactType.WorkerAccount)[inputName]);
  }

  /**
   * Returns true if the current personal ID is other identifier than Finnish Personal Identification Number (keinohetu).
   */
  public get isOtherIdentifier() {
    return EmploymentLogic.isPersonalIdOther(this.current.identity.personalId);
  }

  private _otherEmployments: EmploymentListItem[] | "loading" | undefined;

  /**
   * Gets the list of other employments with this personal ID.
   * First call starts the loading process and returns undefined.
   * Once response is received, the getter returns an array of employments (list items).
   */
  public get otherEmployments(): EmploymentListItem[] | undefined {
    if (!this.current) {
      return undefined;
    }
    if (!this._otherEmployments) {
      if (!this.current.personalInfo.officialId) {
        this._otherEmployments = [];
      } else {
        this._otherEmployments = "loading";
        this.fullApi.getOData({ $filter: `otherPartyInfo/officialId eq '${this.current.personalInfo.officialId}'` }).then((result) => {
          this._otherEmployments = result.items.filter(x => x.id != this.current.id);
        });
      }
    }
    return this._otherEmployments == "loading" ? undefined : this._otherEmployments;
  }

  /** Provides the logic for worker calculations OData UI. */
  public calcListLogic = {
    /**
     * Returns true if the calculation is for another employment relation than the current one.
     * @param calc Calculation to evaluate.
     * @returns True, if the calculation is for another employment relation.
     */
    isNotCurrentEmployment: (calc: CalculationListItem): boolean => {
      return calc.data.employmentId !== this.currentId;
    }
  };
}
